import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, LoginData } from '../types';
import { authApi } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (data: LoginData) => Promise<void>;
  completePasswordChallenge: (data: { email: string; tempPassword: string; newPassword: string }) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // Use same token key as Cognito for consistency
      const token = localStorage.getItem('cognito_access_token');
      const savedUser = localStorage.getItem('user');

      // Clean up legacy token if it exists
      const legacyToken = localStorage.getItem('auth_token');
      if (legacyToken) {
        localStorage.removeItem('auth_token');
        // If we don't have the new token but have legacy, migrate it
        if (!token) {
          localStorage.setItem('cognito_access_token', legacyToken);
        }
      }

      const finalToken = token || legacyToken;
      if (finalToken && savedUser) {
        try {
          setUser(JSON.parse(savedUser));
          // Verify token is still valid
          await authApi.getCurrentUser();
        } catch (error) {
          console.error('Token validation failed:', error);
          localStorage.removeItem('cognito_access_token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (data: LoginData) => {
    try {
      const response = await authApi.login(data);
      // Handle both legacy JWT response and Cognito response formats
      const token = response.token || response.accessToken;
      const user = response.user;
      
      if (!token || !user) {
        throw new Error('Invalid response format from authentication service');
      }
      
      // Use same token key as Cognito for consistency
      localStorage.setItem('cognito_access_token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
    } catch (error) {
      throw error;
    }
  };


  const completePasswordChallenge = async (data: { email: string; tempPassword: string; newPassword: string }) => {
    try {
      const response = await authApi.completePasswordChallenge(data);
      // Handle both legacy JWT response and Cognito response formats
      const token = response.token || response.accessToken;
      const user = response.user;
      
      if (!token || !user) {
        throw new Error('Invalid response format from authentication service');
      }
      
      // Use same token key as Cognito for consistency
      localStorage.setItem('cognito_access_token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    // Remove both legacy and new token keys for complete cleanup
    localStorage.removeItem('auth_token');
    localStorage.removeItem('cognito_access_token');
    localStorage.removeItem('cognito_refresh_token');
    localStorage.removeItem('cognito_id_token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    completePasswordChallenge,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};